import {
  CbhIcon,
  type IconType,
  Illustration,
  type IllustrationType,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardContent, Stack } from "@mui/material";
import { type ReactNode } from "react";

import { useIsSmallHorizontalSpacing } from "../../utils/useIsSmallHorizontalSpacing";
import { useIsSmallVerticalSpacing } from "../../utils/useIsSmallVerticalSpacing";
import { BookabilityCriteriaCardDescriptionText } from "./BookabilityCriteriaCardDescriptionText";
import { BookabilityCriteriaCardTitleText } from "./BookabilityCriteriaCardTitleText";
import { ShiftBottomSheetInnerCard, type ShiftBottomSheetInnerCardIntent } from "./InnerCard";

export interface ShiftBottomSheetBookabilityCriteriaCardProps {
  pill?: ReactNode;
  illustrationType: IllustrationType;
  title: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  intent?: ShiftBottomSheetInnerCardIntent;
  icon?: IconType;
}

/**
 * This is a generic card that is used to display info about unmet bookability criteria
 * All of such cards display similar UI with some custom texts and illustration
 */
export function ShiftBottomSheetBookabilityCriteriaCard(
  props: ShiftBottomSheetBookabilityCriteriaCardProps
) {
  const { title, pill, description, illustrationType, footer, intent, icon } = props;

  const isSmallVerticalSpacing = useIsSmallVerticalSpacing();
  const isSmallHorizontalSpacing = useIsSmallHorizontalSpacing();

  return (
    <ShiftBottomSheetInnerCard intent={intent} sx={{ display: "flex" }}>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          flexGrow: 1,
          py: isSmallVerticalSpacing ? 1 : undefined,
          px: isSmallHorizontalSpacing ? 2 : 6,
        }}
      >
        {isDefined(pill) && <Box sx={{ width: "100%", alignSelf: "flex-start" }}>{pill}</Box>}

        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={isSmallVerticalSpacing ? 2 : 5}
          sx={{
            textAlign: "center",
            textWrap: "balance",
            width: "100%",
            flexGrow: 1,
            mb: isDefined(footer) ? "auto" : undefined,
          }}
        >
          {isDefined(icon) ? (
            <CbhIcon
              type={icon}
              size="xLarge"
              sx={{
                width: "7rem",
                height: "7rem",
              }}
            />
          ) : (
            <Illustration type={illustrationType} />
          )}

          {typeof title === "string" ? (
            <BookabilityCriteriaCardTitleText>{title}</BookabilityCriteriaCardTitleText>
          ) : (
            title
          )}

          {isDefined(description) &&
            (typeof description === "string" ? (
              <BookabilityCriteriaCardDescriptionText>
                {description}
              </BookabilityCriteriaCardDescriptionText>
            ) : (
              description
            ))}
        </Stack>

        {isDefined(footer) && (
          <Box sx={{ width: "100%", mt: isSmallVerticalSpacing ? 2 : 5 }}>{footer}</Box>
        )}
      </CardContent>
    </ShiftBottomSheetInnerCard>
  );
}
